// The action bar: an Instruction, a re-run, and the approval.
//
// The Instruction text lives in this component and nowhere higher up. That is
// not tidiness: the alignment above is a virtualized list of up to a few
// hundred rows, and holding the field's value in the screen would re-render it
// on every keystroke.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/control/Button";
import { TextInput } from "../../../components/control/TextInput";
import { WorkspaceActions } from "../../../components/layout/WorkspaceActions";

/** The `r` shortcut focuses the field by id: TextInput renders an input, not a ref-forwarding component. */
export const INSTRUCTION_FIELD_ID = "review-instruction";

export interface ActionBarProps {
  /** False while the Chapter is refining, rendering, or excluded. */
  readonly enabled: boolean;
  readonly rerunning: boolean;
  readonly approving: boolean;
  /** False when the Chapter has no script yet, or is already Approved at this version. */
  readonly canApprove: boolean;
  /** True on the Calibration Chapter: approving it releases Refinement of every other Chapter. */
  readonly calibration: boolean;
  /** Calls back only once the re-run has been accepted, which is when the field clears. */
  readonly onRerun: (instruction: string | null, onAccepted: () => void) => void;
  readonly onApprove: () => void;
}

export function ActionBar({
  enabled,
  rerunning,
  approving,
  canApprove,
  calibration,
  onRerun,
  onApprove,
}: ActionBarProps) {
  const { t } = useTranslation("review");
  const [instruction, setInstruction] = useState("");
  const trimmed = instruction.trim();

  return (
    <WorkspaceActions>
      <div
        data-component="ReviewActionBar"
        className="flex w-full min-w-0 shrink-0 flex-wrap items-center gap-2.5 bg-surface"
      >
        {/* Enter deliberately does nothing here. A re-run is a paid LLM call, and
          DESIGN.md puts a gate in front of every spend the Author might not
          expect; a stray Enter in a text field is not that gate. */}
        <TextInput
          id={INSTRUCTION_FIELD_ID}
          value={instruction}
          disabled={!enabled}
          aria-label={t("instructionLabel")}
          placeholder={t("instructionPlaceholder")}
          className="min-w-0 flex-1"
          onChange={(event) => setInstruction(event.target.value)}
        />
        <Button
          variant="secondary"
          disabled={!enabled}
          busy={rerunning}
          // The field is cleared from the answer, not from the click. A 409
          // because the render started, a 500, or a browser that is offline
          // would otherwise take the sentence the Author composed with it and
          // leave them nothing to retry from.
          onClick={() => onRerun(trimmed === "" ? null : trimmed, () => setInstruction(""))}
        >
          {t("rerun")}
        </Button>
        {/* On the Calibration Chapter this button is the gate DESIGN.md step 4
          describes: it starts a paid Refinement of every other Chapter. It says
          so rather than reading "Kapitel freigeben" like any other Chapter. */}
        <Button
          className="max-w-full whitespace-normal text-center"
          disabled={!enabled || !canApprove}
          busy={approving}
          onClick={onApprove}
        >
          {calibration ? t("approveCalibration") : t("approve")}
        </Button>
      </div>
    </WorkspaceActions>
  );
}
